// YEETLAB: full-screen text displays (Information and Announcements).
//
// These used to be faked by the parent page: it cloned the local forecast
// element, paused the rotation by clicking the play/pause button, showed the
// clone on a setTimeout chain, then clicked play again. That fought the
// navigation engine, was invisible to the settings list, and broke any time the
// real rotation moved on its own. They are real registered displays now, so they
// get a rotation slot, their own duration, and a settings checkbox like every
// other screen.
//
// The words are not in this bundle. The site template reads them from
// data/yeetcast-text.json and hands them over as window.YC_TEXT, so changing
// what the screens say is a JSON edit and a deploy, never a rebuild. The
// defaults below only show if that file is missing or broken.

import STATUS from './status.mjs';
import WeatherDisplay from './weatherdisplay.mjs';
import { registerDisplay } from './navigation.mjs';

const DEFAULTS = {
	information: {
		title: ['YeetLab', 'Information'],
		seconds: 6,
		pages: [['YEETLAB <3 U']],
	},
	// built and ready, shipped off by default until there is something to announce
	announcements: {
		title: ['YeetLab', 'Announcements'],
		seconds: 6,
		pages: [],
	},
};

// one tick per second, so a page's duration is just its seconds
const TICK_MS = 1000;

const cleanLines = (lines) => (Array.isArray(lines) ? lines : [lines])
	.filter((line) => typeof line === 'string' || typeof line === 'number')
	.map((line) => String(line));

const cleanTitle = (title, fallback) => {
	if (!Array.isArray(title)) return fallback;
	const [top = '', bottom = ''] = title.map((part) => String(part ?? ''));
	return [top, bottom];
};

const cleanSeconds = (seconds, fallback) => {
	const value = Number(seconds);
	return Number.isFinite(value) && value >= 2 ? Math.min(Math.round(value), 60) : fallback;
};

// A page may be written as a plain list of lines, or as
// { lines, title: [top, bottom], seconds } when it needs its own heading or pace.
const readSection = (key) => {
	const fallback = DEFAULTS[key];
	const section = window.YC_TEXT?.[key];
	if (!section || typeof section !== 'object') return { ...fallback, pages: fallback.pages.map((lines) => ({ lines, title: fallback.title, seconds: fallback.seconds })) };

	const title = cleanTitle(section.title, fallback.title);
	const seconds = cleanSeconds(section.seconds, fallback.seconds);
	const pages = (Array.isArray(section.pages) ? section.pages : [])
		.map((page) => {
			if (Array.isArray(page) || typeof page === 'string') return { lines: cleanLines(page), title, seconds };
			if (!page || typeof page !== 'object') return null;
			return {
				lines: cleanLines(page.lines ?? []),
				title: cleanTitle(page.title, title),
				seconds: cleanSeconds(page.seconds, seconds),
			};
		})
		.filter((page) => page && page.lines.length);
	return { enabled: section.enabled, title, pages };
};

class TextDisplay extends WeatherDisplay {
	constructor(navId, elemId, name, key) {
		const section = readSection(key);
		const defaultEnabled = key === 'information' ? section.enabled !== false : section.enabled === true;
		super(navId, elemId, name, defaultEnabled);

		this.key = key;
		this.pages = section.pages;
		this.timing.baseDelay = TICK_MS;
		this.setPageTiming();
	}

	setPageTiming() {
		// per-page durations; an empty list still needs a valid delay array
		this.timing.delay = this.pages.length ? this.pages.map((page) => page.seconds) : [1];
		this.timing.totalScreens = this.pages.length;
	}

	// no network data to fetch, but the display still has to report a status so
	// the progress screen and the rotation know whether to include it
	async getData(weatherParameters, refresh) {
		if (!super.getData(weatherParameters, refresh)) return;

		this.pages = readSection(this.key).pages;
		this.setPageTiming();

		// nothing to show: report it and let navigation skip the screen
		if (this.pages.length === 0) {
			this.timing.totalScreens = 0;
			this.setStatus(STATUS.noData);
			return;
		}

		this.calcNavTiming();
		this.setStatus(STATUS.loaded);
	}

	async drawCanvas() {
		super.drawCanvas();

		const page = this.pages[this.screenIndex] ?? this.pages[0];
		if (page) {
			const [top, bottom] = page.title;
			const topElem = this.elem.querySelector('.header .title .top');
			const bottomElem = this.elem.querySelector('.header .title .bottom');
			if (topElem) topElem.textContent = top;
			if (bottomElem) bottomElem.textContent = bottom;
		}

		const messages = this.elem.querySelector('.messages');
		if (messages) {
			messages.replaceChildren(...(page?.lines ?? []).map((text) => {
				const line = document.createElement('div');
				line.classList.add('message');
				line.textContent = text;
				return line;
			}));
		}

		this.finishDraw();
	}
}

registerDisplay(new TextDisplay(12, 'yeetcast-info', 'Information', 'information'));
registerDisplay(new TextDisplay(13, 'yeetcast-announcements', 'Announcements', 'announcements'));
